/**
 * Analyse GPX déportée hors du fil principal.
 *
 * `parseGpx` parcourt le fichier caractère par caractère et alloue un objet par
 * point : sur une étape de deux cent mille points, l'onglet ne rend plus la
 * main pendant plusieurs secondes — assez pour qu'on croie l'application
 * plantée, et cela à l'import comme à chaque redémarrage, où toutes les traces
 * conservées sont relues.
 *
 * Ce que le travailleur renvoie traverse un clone structuré : le résultat ne
 * contient donc que des objets simples, des tableaux, des chaînes et une `Map`
 * — c'est déjà le cas de `ParseResult`, et cela doit le rester. Une classe ou
 * une fonction qui s'y glisserait ferait échouer l'envoi, pas la compilation.
 */

import { parseGpx, type ParseResult } from './gpxParser'
import { runInWorker } from './workerBridge'

export interface GpxParseRequest {
  readonly source: string
  readonly fileName?: string | null
  readonly defaultTrackName?: string
}

/** Exécute une analyse, quel que soit le fil sur lequel on se trouve. */
export function runGpxParseTask(request: GpxParseRequest): ParseResult {
  return parseGpx(request.source, {
    fileName: request.fileName,
    defaultTrackName: request.defaultTrackName,
  })
}

/** Analyse un document GPX sur un fil séparé, avec repli sur place. */
export function parseGpxAsync(request: GpxParseRequest): Promise<ParseResult> {
  return runInWorker(
    () =>
      new Worker(new URL('./gpxWorker.entry.ts', import.meta.url), {
        type: 'module',
      }),
    request,
    runGpxParseTask,
  )
}
